/**
 * Hand inference scheduling.
 *
 * Two execution strategies, chosen by capability detection:
 *
 *  1. **worker** — `MediaStreamTrackProcessor` hands `VideoFrame`s directly to a worker that
 *     owns the landmarker. The main thread does zero per-frame tracking work.
 *  2. **main-thread** — `requestVideoFrameCallback` (falling back to `requestAnimationFrame`)
 *     drives `detectForVideo` on the video element, throttled to the inference budget so it
 *     never runs once per rendered frame.
 *
 * Either way inference is decoupled from rendering: the render loop is a separate rAF owned
 * by the app, and this class only emits results as they arrive.
 *
 * The budget adapts: when inference consistently overruns, the target rate drops (and the
 * app separately drops capture resolution) rather than letting the whole page stutter.
 */

import type { HandLandmarker } from '@mediapipe/tasks-vision';
import type { AppErrorInfo } from '../app/AppStateMachine';
import {
  DEFAULT_LANDMARKER_TUNING,
  LandmarkerSetupError,
  readHandedness,
  readHandednessScore,
} from './handLandmarkerSetup';
import type { LandmarkerConfig } from './handLandmarkerSetup';
import HandTrackerWorker from './handTracker.worker?worker';
import { MAX_HANDS, createSamplePool, decodeHands } from './handFrameCodec';
import type { WorkerInbound, WorkerOutbound } from './handTrackerProtocol';
import type { HandLandmarkSample, InferenceResult } from './types';
import { clamp } from './types';

export type InferenceMode = 'worker' | 'main-thread' | 'idle';

export interface HandTrackerEvents {
  onResult(result: InferenceResult): void;
  onError(info: AppErrorInfo): void;
  onModeChange(mode: InferenceMode): void;
}

export interface HandTrackerOptions {
  /** Directory containing the MediaPipe WASM runtime. */
  wasmBasePath?: string;
  /** URL of `hand_landmarker.task`. */
  modelAssetPath?: string;
  /** Upper bound on inference rate. The brief targets 20-30 Hz. */
  targetHz?: number;
  minHz?: number;
  maxHz?: number;
  /** Set false to force the main-thread path (used by tests and for debugging). */
  preferWorker?: boolean;
  delegate?: 'GPU' | 'CPU';
}

/**
 * `requestVideoFrameCallback` is declared as required by lib.dom but is genuinely absent in
 * Firefox, so every call site guards with a runtime `typeof` check.
 */
type OptionalRvfc = Partial<
  Pick<HTMLVideoElement, 'requestVideoFrameCallback' | 'cancelVideoFrameCallback'>
>;

/** Resolve an asset URL against the app's base, honouring build-time overrides. */
function resolveAssetUrl(relative: string, override: string | undefined): string {
  if (override && override.length > 0) return override;
  const base =
    typeof document !== 'undefined' && document.baseURI
      ? document.baseURI
      : (globalThis.location?.href ?? 'http://localhost/');
  return new URL(relative, base).href;
}

export function defaultWasmBasePath(): string {
  // No trailing slash: MediaPipe builds `${base}/${file}` itself.
  return resolveAssetUrl(
    'mediapipe-wasm',
    import.meta.env?.VITE_MEDIAPIPE_WASM_BASE as string | undefined,
  ).replace(/\/+$/, '');
}

export function defaultModelPath(): string {
  return resolveAssetUrl(
    'models/hand_landmarker.task',
    import.meta.env?.VITE_HAND_MODEL_URL as string | undefined,
  );
}

/** Whether the zero-copy worker path is available on this platform. */
export function supportsWorkerFramePath(): boolean {
  return (
    typeof Worker === 'function' &&
    typeof (globalThis as { MediaStreamTrackProcessor?: unknown }).MediaStreamTrackProcessor ===
      'function'
  );
}

/**
 * Whether the worker path is worth attempting at all.
 *
 * Vite serves workers as ES modules during development regardless of `worker.format`
 * (`workerType = isBuild ? ... : "module"`), and MediaPipe's WASM loader only publishes its
 * `ModuleFactory` global through `importScripts`, which a module worker does not have. So in
 * dev the worker always fails — after spending several seconds downloading the model. Skip
 * it and go straight to the main thread; `npm run preview` exercises the real worker path.
 *
 * `?forceWorker` overrides this if you want to watch it fail.
 */
export function shouldAttemptWorker(): boolean {
  if (!supportsWorkerFramePath()) return false;
  if (!import.meta.env?.DEV) return true;
  const search = globalThis.location?.search ?? '';
  return new URLSearchParams(search).has('forceWorker');
}

interface TrackProcessorCtor {
  new (init: { track: MediaStreamTrack }): { readable: ReadableStream<VideoFrame> };
}

export class HandTracker {
  private readonly events: HandTrackerEvents;

  private readonly config: LandmarkerConfig;

  private readonly preferWorker: boolean;

  private readonly minHz: number;

  private readonly maxHz: number;

  private worker: Worker | null = null;

  private landmarker: HandLandmarker | null = null;

  private currentMode: InferenceMode = 'idle';

  private initialized = false;

  private disposed = false;

  private targetHzValue: number;

  private video: HTMLVideoElement | null = null;

  private rvfcHandle: number | null = null;

  private rafHandle: number | null = null;

  private lastInferenceAt = 0;

  private syntheticTimestamp = 0;

  private paused = false;

  /** Rolling mean of recent inference durations, used to adapt the budget. */
  private averageInferenceMs = 0;

  private adaptCooldownUntil = 0;

  private readonly samplePool = createSamplePool(MAX_HANDS);

  private readonly sampleView: HandLandmarkSample[] = [];

  private readonly resultOut: InferenceResult = {
    hands: [],
    timestamp: 0,
    inferenceMs: 0,
    frameWidth: 0,
    frameHeight: 0,
  };

  constructor(events: HandTrackerEvents, options: HandTrackerOptions = {}) {
    this.events = events;
    this.preferWorker = options.preferWorker ?? true;
    this.minHz = options.minHz ?? 10;
    this.maxHz = options.maxHz ?? 30;
    this.targetHzValue = clamp(options.targetHz ?? 26, this.minHz, this.maxHz);
    this.config = {
      wasmBasePath: options.wasmBasePath ?? defaultWasmBasePath(),
      modelAssetPath: options.modelAssetPath ?? defaultModelPath(),
      delegate: options.delegate ?? 'GPU',
      ...DEFAULT_LANDMARKER_TUNING,
    };
  }

  get mode(): InferenceMode {
    return this.currentMode;
  }

  get targetHz(): number {
    return this.targetHzValue;
  }

  get ready(): boolean {
    return this.initialized;
  }

  setTargetHz(hz: number): void {
    const next = clamp(hz, this.minHz, this.maxHz);
    if (Math.abs(next - this.targetHzValue) < 0.5) return;
    this.targetHzValue = next;
    this.worker?.postMessage({ type: 'setHz', hz: next } satisfies WorkerInbound);
  }

  /** Set detection thresholds at runtime. Cheap on the main-thread path, restarts the worker. */
  async setConfidence(detection: number, presence: number, tracking: number): Promise<void> {
    this.config.minHandDetectionConfidence = detection;
    this.config.minHandPresenceConfidence = presence;
    this.config.minTrackingConfidence = tracking;
    if (this.landmarker) {
      await this.landmarker.setOptions({
        minHandDetectionConfidence: detection,
        minHandPresenceConfidence: presence,
        minTrackingConfidence: tracking,
      });
    }
  }

  /**
   * Load the model. Resolves once inference is possible; rejects only when neither strategy
   * can be initialised.
   */
  async initialize(): Promise<void> {
    if (this.initialized || this.disposed) return;

    if (this.preferWorker && shouldAttemptWorker()) {
      const ok = await this.initializeWorker();
      if (ok) {
        this.initialized = true;
        this.setMode('worker');
        return;
      }
      // Fall through to the main-thread path rather than failing outright.
    }

    try {
      // Only the fallback path pays for the MediaPipe runtime on the main thread.
      const { createHandLandmarker } = await import('./createHandLandmarker');
      this.landmarker = await createHandLandmarker(this.config);
      this.initialized = true;
      this.setMode('main-thread');
    } catch (error) {
      this.emitSetupError(error);
      throw error;
    }
  }

  private emitSetupError(error: unknown): void {
    const failure = error instanceof LandmarkerSetupError ? error.failure : 'unknown';
    const message = error instanceof Error ? error.message : String(error);
    this.events.onError({
      kind:
        failure === 'model-download'
          ? 'model-download'
          : failure === 'wasm-init'
            ? 'wasm-init'
            : 'unknown',
      message,
      recoverable: true,
      cause: error,
    });
  }

  private async initializeWorker(): Promise<boolean> {
    let worker: Worker;
    try {
      worker = new HandTrackerWorker();
    } catch (error) {
      console.warn('[tracking] worker construction failed; using the main thread', error);
      return false;
    }

    const ready = await new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        cleanup();
        resolve(false);
      }, 20_000);

      const onMessage = (event: MessageEvent<WorkerOutbound>): void => {
        const message = event.data;
        if (message.type === 'ready') {
          cleanup();
          resolve(true);
        } else if (message.type === 'error') {
          cleanup();
          console.warn(`[tracking] worker init failed (${message.kind}): ${message.message}`);
          resolve(false);
        }
      };
      const onError = (): void => {
        cleanup();
        resolve(false);
      };
      const cleanup = (): void => {
        clearTimeout(timer);
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
      };

      worker.addEventListener('message', onMessage);
      worker.addEventListener('error', onError);
      worker.postMessage({ type: 'init', config: this.config } satisfies WorkerInbound);
    });

    if (!ready) {
      worker.terminate();
      return false;
    }

    worker.addEventListener('message', this.handleWorkerMessage);
    worker.addEventListener('error', this.handleWorkerError);
    worker.postMessage({ type: 'setHz', hz: this.targetHzValue } satisfies WorkerInbound);
    this.worker = worker;
    return true;
  }

  private readonly handleWorkerMessage = (event: MessageEvent<WorkerOutbound>): void => {
    const message = event.data;
    if (message.type === 'result') {
      decodeHands(message.data, message.handCount, this.samplePool, this.sampleView);
      this.emitResult(
        this.sampleView,
        message.timestamp,
        message.inferenceMs,
        message.frameWidth,
        message.frameHeight,
      );
      return;
    }
    if (message.type === 'error') {
      this.events.onError({
        kind: message.kind === 'unknown' ? 'unknown' : message.kind,
        message: message.message,
        recoverable: true,
      });
    }
  };

  private readonly handleWorkerError = (event: ErrorEvent): void => {
    console.error('[tracking] worker error', event.message);
    // Losing the worker must not kill the instrument: tear it down and continue on the
    // main thread if we can build a landmarker there.
    void this.degradeToMainThread();
  };

  private async degradeToMainThread(): Promise<void> {
    if (this.disposed || this.currentMode !== 'worker') return;
    this.teardownWorker();
    try {
      const { createHandLandmarker } = await import('./createHandLandmarker');
      this.landmarker = await createHandLandmarker(this.config);
      this.setMode('main-thread');
      if (this.video) this.startMainThreadLoop();
    } catch (error) {
      this.emitSetupError(error);
      this.setMode('idle');
    }
  }

  /** Begin consuming frames. Safe to call again after a camera switch. */
  attach(video: HTMLVideoElement, track: MediaStreamTrack | null): void {
    if (this.disposed) return;
    this.detach();
    this.video = video;
    this.paused = false;

    if (this.currentMode === 'worker' && this.worker && track) {
      const Processor = (globalThis as { MediaStreamTrackProcessor?: TrackProcessorCtor })
        .MediaStreamTrackProcessor;
      if (Processor) {
        try {
          const processor = new Processor({ track });
          this.worker.postMessage(
            { type: 'stream', stream: processor.readable } satisfies WorkerInbound,
            [processor.readable],
          );
          return;
        } catch (error) {
          console.warn('[tracking] frame processor unavailable; using the main thread', error);
          void this.degradeToMainThread();
          return;
        }
      }
    }

    if (this.currentMode === 'main-thread') this.startMainThreadLoop();
  }

  detach(): void {
    this.stopMainThreadLoop();
    this.worker?.postMessage({ type: 'stop' } satisfies WorkerInbound);
    this.video = null;
  }

  /** Stop inference without tearing anything down (page hidden). */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.stopMainThreadLoop();
    this.worker?.postMessage({ type: 'setHz', hz: this.minHz } satisfies WorkerInbound);
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.worker?.postMessage({ type: 'setHz', hz: this.targetHzValue } satisfies WorkerInbound);
    if (this.currentMode === 'main-thread' && this.video) this.startMainThreadLoop();
  }

  private setMode(mode: InferenceMode): void {
    if (this.currentMode === mode) return;
    this.currentMode = mode;
    this.events.onModeChange(mode);
  }

  private startMainThreadLoop(): void {
    const video = this.video;
    if (!video || this.paused || this.disposed) return;
    this.stopMainThreadLoop();

    const rvfc = (video as OptionalRvfc).requestVideoFrameCallback;
    if (typeof rvfc === 'function') {
      const request = rvfc.bind(video);
      const step: VideoFrameRequestCallback = (_now, metadata) => {
        this.rvfcHandle = null;
        this.runMainThreadInference(metadata.width, metadata.height);
        if (!this.paused && this.video) this.rvfcHandle = request(step);
      };
      this.rvfcHandle = request(step);
      return;
    }

    // No rVFC (Firefox today): fall back to rAF and rely on the throttle below.
    const tick = (): void => {
      this.rafHandle = null;
      this.runMainThreadInference(video.videoWidth, video.videoHeight);
      if (!this.paused && this.video) this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopMainThreadLoop(): void {
    const video = this.video;
    const cancel = video ? (video as OptionalRvfc).cancelVideoFrameCallback : undefined;
    if (this.rvfcHandle !== null && video && typeof cancel === 'function') {
      cancel.call(video, this.rvfcHandle);
    }
    this.rvfcHandle = null;
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
  }

  private runMainThreadInference(frameWidth: number, frameHeight: number): void {
    const video = this.video;
    const landmarker = this.landmarker;
    if (!video || !landmarker || this.paused) return;
    if (video.readyState < 2 || video.videoWidth === 0) return;

    const now = performance.now();
    const minInterval = 1000 / Math.max(4, this.targetHzValue);
    if (now - this.lastInferenceAt < minInterval) return;
    this.lastInferenceAt = now;

    this.syntheticTimestamp = Math.max(this.syntheticTimestamp + 1, Math.round(now));

    let inferenceMs = 0;
    try {
      const started = performance.now();
      const result = landmarker.detectForVideo(video, this.syntheticTimestamp);
      inferenceMs = performance.now() - started;

      this.sampleView.length = 0;
      const handCount = Math.min(result.landmarks.length, MAX_HANDS);
      for (let i = 0; i < handCount; i += 1) {
        const landmarks = result.landmarks[i];
        const sample = this.samplePool[i];
        if (!landmarks || !sample) continue;
        for (let j = 0; j < sample.landmarks.length; j += 1) {
          const source = landmarks[j];
          const target = sample.landmarks[j];
          if (!source || !target) continue;
          target.x = source.x;
          target.y = source.y;
          target.z = source.z;
        }
        sample.handedness = readHandedness(result, i);
        sample.score = readHandednessScore(result, i);
        this.sampleView.push(sample);
      }
    } catch (error) {
      console.warn('[tracking] inference failed for one frame', error);
      return;
    }

    this.emitResult(
      this.sampleView,
      now,
      inferenceMs,
      frameWidth || video.videoWidth,
      frameHeight || video.videoHeight,
    );
  }

  private emitResult(
    hands: HandLandmarkSample[],
    timestamp: number,
    inferenceMs: number,
    frameWidth: number,
    frameHeight: number,
  ): void {
    this.adaptBudget(inferenceMs, timestamp);
    this.resultOut.hands = hands;
    this.resultOut.timestamp = timestamp;
    this.resultOut.inferenceMs = inferenceMs;
    this.resultOut.frameWidth = frameWidth;
    this.resultOut.frameHeight = frameHeight;
    this.events.onResult(this.resultOut);
  }

  /**
   * Keep inference inside roughly half of its own frame interval. When it overruns, back off
   * the rate; when there is comfortable headroom, creep back up. The cooldown stops the two
   * rules from oscillating.
   */
  private adaptBudget(inferenceMs: number, now: number): void {
    this.averageInferenceMs =
      this.averageInferenceMs === 0
        ? inferenceMs
        : this.averageInferenceMs * 0.9 + inferenceMs * 0.1;
    if (now < this.adaptCooldownUntil) return;

    const interval = 1000 / this.targetHzValue;
    if (this.averageInferenceMs > interval * 0.8 && this.targetHzValue > this.minHz) {
      this.setTargetHz(this.targetHzValue - 4);
      this.adaptCooldownUntil = now + 2000;
    } else if (this.averageInferenceMs < interval * 0.35 && this.targetHzValue < this.maxHz) {
      this.setTargetHz(this.targetHzValue + 2);
      this.adaptCooldownUntil = now + 3000;
    }
  }

  private teardownWorker(): void {
    const worker = this.worker;
    this.worker = null;
    if (!worker) return;
    worker.removeEventListener('message', this.handleWorkerMessage);
    worker.removeEventListener('error', this.handleWorkerError);
    try {
      worker.postMessage({ type: 'dispose' } satisfies WorkerInbound);
    } catch {
      // Worker already gone.
    }
    worker.terminate();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.detach();
    this.teardownWorker();
    try {
      this.landmarker?.close();
    } catch {
      // Already closed.
    }
    this.landmarker = null;
    this.initialized = false;
    this.setMode('idle');
  }
}
